import { useEffect, useRef } from 'react';

type ItemType = 'links' | 'buttons';

const selectors: Record<ItemType, string> = {
  links: 'a[href]',
  buttons: 'div[data-state] button',
};

const isFocusableItem = (
  element: Element,
  itemType: ItemType,
): element is HTMLAnchorElement | HTMLButtonElement => {
  if (itemType === 'links') {
    return element instanceof HTMLAnchorElement;
  }
  return element instanceof HTMLButtonElement;
};

// TODO: When Radix UI fix issues with keyboard navigation in Shadow DOM, this
// hook can be removed (CL-154).
const useMenuKeyboardNavigation = (itemType: ItemType = 'links') => {
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = menuRef.current;
    if (!element) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const isTabForward = event.key === 'Tab' && !event.shiftKey;
      const isTabBackward = event.key === 'Tab' && event.shiftKey;

      const items = [...element.querySelectorAll(selectors[itemType])];
      items.forEach((item) => {
        item.setAttribute('tabindex', '-1');
      });

      const triggerButton = element.querySelector('button[aria-expanded]');
      const activeItem = element.querySelector(':focus-visible');
      const currentIndex = activeItem ? items.indexOf(activeItem) : -1;
      const firstItemIsActive = activeItem === items[0];
      const lastItemIsActive = activeItem === items[items.length - 1];

      if (triggerButton === activeItem) {
        if (triggerButton?.getAttribute('aria-expanded') === 'false') {
          return undefined;
        }
        const item = items[0];
        if (
          (event.key === 'ArrowDown' || isTabForward) &&
          isFocusableItem(item, itemType)
        ) {
          event.preventDefault();
          item.focus();
        }
        return undefined;
      }

      if (isTabForward && lastItemIsActive) {
        return undefined;
      }

      if (['ArrowDown', 'ArrowRight'].includes(event.key) || isTabForward) {
        event.preventDefault();
        const lastIndex = items.length - 1;
        const nextIndex =
          currentIndex === -1 ? 0 : Math.min(currentIndex + 1, lastIndex);
        const nextItem = items[nextIndex];
        if (isFocusableItem(nextItem, itemType)) {
          nextItem.focus();
        }
      }

      if (
        ['ArrowUp', 'ArrowLeft'].includes(event.key) ||
        (isTabBackward && !firstItemIsActive)
      ) {
        event.preventDefault();
        if (currentIndex > 0) {
          const prevIndex = currentIndex - 1;
          const prevItem = items[prevIndex];
          if (isFocusableItem(prevItem, itemType)) {
            prevItem.focus();
          }
        }
      }

      return undefined;
    };

    element.addEventListener('keydown', handleKeyDown);
    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return menuRef;
};

export default useMenuKeyboardNavigation;
